/**
 * Monte Carlo worker client — single in-flight request, latest-wins: if a new
 * simulation is requested while one runs, the result of the stale one is
 * dropped (the UI only ever shows probabilities of the latest state).
 */
import type { SimulationInput, SimulationOutput } from "../engine/montecarlo.ts";
import type { McWorkerRequest, McWorkerResponse } from "./mc.worker.ts";

export interface McResult {
  readonly output: SimulationOutput;
  readonly elapsedMs: number;
}

export class McClient {
  private readonly worker: Worker;
  private nextId = 1;
  private latestRequested = 0;
  private pending = new Map<number, { resolve: (r: McResult) => void; reject: (e: Error) => void }>();

  constructor() {
    this.worker = new Worker(new URL("./mc.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (event: MessageEvent<McWorkerResponse>) => {
      const { requestId, type, output, message, elapsedMs } = event.data;
      const entry = this.pending.get(requestId);
      if (!entry) return;
      this.pending.delete(requestId);
      if (type === "result" && output && requestId === this.latestRequested) {
        entry.resolve({ output, elapsedMs });
      } else if (type === "error") {
        entry.reject(new Error(message ?? "erreur worker"));
      } else {
        entry.reject(new Error("résultat obsolète (nouvelle simulation lancée)"));
      }
    };
  }

  simulate(input: SimulationInput): Promise<McResult> {
    const requestId = this.nextId++;
    this.latestRequested = requestId;
    const request: McWorkerRequest = { type: "simulate", requestId, input };
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject });
      this.worker.postMessage(request);
    });
  }
}

/** 20 000 tirages desktop, 10 000 mobile (détection simple, specs/03). */
export function defaultSimCount(): number {
  const coarse = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;
  const small = typeof matchMedia !== "undefined" && matchMedia("(max-width: 600px)").matches;
  return coarse && small ? 10_000 : 20_000;
}
